import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link, Image } from './common/NextAdapters';
import { Icon } from '@iconify/react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Carousel, CarouselContent, CarouselItem } from './ui/carousel';
import { MyListButton } from './my-list-button';

export function HeroCarousel({ titles = [] }) {
  const [api, setApi] = useState(null);
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setSelected(api.selectedScrollSnap());
    onSelect();
    api.on('select', onSelect);
    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  const scrollPrev = useCallback(() => api?.scrollPrev(), [api]);
  const scrollNext = useCallback(() => api?.scrollNext(), [api]);

  const handleTrailerEnded = useCallback(() => {
    if (!api) return;
    if (selected >= titles.length - 1) {
      api.scrollTo(0);
    } else {
      api.scrollNext();
    }
  }, [api, selected, titles.length]);

  if (!titles || titles.length === 0) return null;

  return (
    <section aria-label="Featured on Kast" className="relative -mt-16 w-full overflow-hidden bg-[#05070a]">
      <Carousel
        setApi={setApi}
        opts={{ loop: titles.length > 1 }}
        className="w-full"
      >
        <CarouselContent className="ml-0">
          {titles.map((title, index) => (
            <CarouselItem key={title.id || title.Id || index} className="pl-0">
              <HeroSlide
                title={title}
                index={index}
                priority={index === 0}
                isActive={index === selected}
                onTrailerEnded={handleTrailerEnded}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {/* Prev / Next — sleek Prime Video style chevrons */}
      {titles.length > 1 && (
        <>
          <button
            type="button"
            onClick={scrollPrev}
            aria-label="Previous"
            className="absolute left-0 top-1/2 -translate-y-1/2 z-30 w-12 h-24 flex items-center justify-center text-white/60 hover:text-white transition-colors duration-200 cursor-pointer focus-visible:outline-none"
          >
            <svg width="14" height="28" viewBox="0 0 14 28" fill="none">
              <path d="M11 4L3 14L11 24" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            type="button"
            onClick={scrollNext}
            aria-label="Next"
            className="absolute right-0 top-1/2 -translate-y-1/2 z-30 w-12 h-24 flex items-center justify-center text-white/60 hover:text-white transition-colors duration-200 cursor-pointer focus-visible:outline-none"
          >
            <svg width="14" height="28" viewBox="0 0 14 28" fill="none">
              <path d="M3 4L11 14L3 24" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </>
      )}

      {/* Dot indicators */}
      {titles.length > 1 && (
        <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 sm:bottom-8">
          <div className="pointer-events-auto mx-auto flex max-w-[1600px] justify-center gap-1.5">
            {titles.map((title, index) => (
              <button
                key={title.id || title.Id || index}
                type="button"
                onClick={() => api?.scrollTo(index)}
                aria-label={`Show ${title.title || title.Title}`}
                aria-current={index === selected}
                className="group/dot size-5 flex items-center justify-center focus-visible:outline-none cursor-pointer"
              >
                <span
                  className={cn(
                    'block rounded-full transition-all duration-300',
                    index === selected
                      ? 'w-2.5 h-2.5 bg-white shadow-[0_0_8px_rgba(255,255,255,0.8)]'
                      : 'w-1.5 h-1.5 bg-white/35 group-hover/dot:bg-white/60'
                  )}
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function HeroSlide({ title, priority, isActive, onTrailerEnded, index = 0 }) {
  const videoRef = useRef(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  const toggleMute = useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!videoRef.current) return;
    const next = !videoRef.current.muted;
    videoRef.current.muted = next;
    setIsMuted(next);
  }, []);

  const id = title.id || title.Id || title.MovieId;
  const name = title.title || title.Title || title.Name || 'Featured Title';
  const thumbnail = title.thumbnail || title.BannerUrl || title.ThumbnailUrl || title.CoverUrl;
  const description = title.description || title.Description || title.Synopsis;
  const trailer = title.trailer || title.TrailerUrl;

  // 5-second delay before video plays
  useEffect(() => {
    if (!videoRef.current || !trailer) return;

    if (isActive) {
      setVideoPlaying(false);
      const timer = setTimeout(() => {
        if (!videoRef.current) return;
        videoRef.current.currentTime = 0;
        videoRef.current
          ?.play()
          .then(() => setVideoPlaying(true))
          .catch(() => setVideoError(true));
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
      setVideoPlaying(false);
    }
  }, [isActive, trailer]);

  return (
    <div className="relative h-[85vh] max-h-[850px] min-h-[520px] w-full bg-[#05070a] overflow-hidden">
      {/* 
        1. Base Poster Image:
        Visible only during the initial 3-second delay or when video is not playing.
        When trailer starts playing, poster fades to 0 opacity so NO gray background ghost shows.
      */}
      <div
        className={cn(
          'absolute inset-0 transition-opacity duration-1000',
          videoPlaying ? 'opacity-0 pointer-events-none' : 'opacity-100'
        )}
      >
        {thumbnail ? (
          <Image
            src={thumbnail}
            alt={name}
            fill
            priority={priority}
            sizes="100vw"
            className="object-cover object-center"
          />
        ) : (
          <div className="absolute inset-0 bg-[#05070a]" />
        )}
        {/* Subtle dark vignettes for poster readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#05070a] via-[#05070a]/75 to-transparent w-[60%] sm:w-[50%]" />
        <div className="absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-[#05070a] to-transparent" />
        <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-[#05070a]/80 to-transparent" />
      </div>

      {/*
        2. Trailer Video:
        Full-bleed like Prime Video's hero — no narrow clipped box, so there's
        no container edge to create a seam. The video fills the whole slide;
        a gradient overlay (matching the poster's own fade) dims the left
        side for text legibility instead of masking/cutting the video itself.
      */}
      {trailer && !videoError && (
        <div
          className={cn(
            'absolute inset-0 overflow-hidden transition-opacity duration-1000 z-[1]',
            videoPlaying ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}
        >
          <video
            ref={videoRef}
            src={trailer}
            muted={isMuted}
            playsInline
            preload="metadata"
            onError={() => setVideoError(true)}
            onPlay={() => setVideoPlaying(true)}
            onPause={() => setVideoPlaying(false)}
            onEnded={() => {
              setVideoPlaying(false);
              onTrailerEnded?.();
            }}
            className="w-full h-full object-cover object-center"
          />
          {/* Gray overlay while the trailer plays */}
          <div className="absolute inset-0 bg-black/30 pointer-events-none" />
          {/* Left-side fade into the solid black background, for text legibility */}
          <div className="absolute inset-y-0 left-0 w-[60%] sm:w-[50%] bg-gradient-to-r from-[#05070a] via-[#05070a]/75 to-transparent pointer-events-none" />
          <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-[#05070a]/90 via-[#05070a]/40 to-transparent pointer-events-none" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#05070a] via-[#05070a]/60 to-transparent pointer-events-none" />
        </div>
      )}

      {/* 3. Left-side Content Overlay (Pure black backdrop when playing) */}
      <div className="relative flex h-full items-end pb-24 sm:items-center sm:pb-0 z-10">
        <div className="mx-auto w-full max-w-[1600px] gutter pt-16">
          <div className="max-w-xl">
            {/* Title */}
            <h1 className="text-5xl sm:text-7xl lg:text-8xl font-black tracking-tight text-white display uppercase leading-none drop-shadow-md">
              {name}
            </h1>

            {/* Description */}
            {description && (
              <p className="mt-4 line-clamp-3 max-w-[46ch] text-lg sm:text-xl leading-relaxed text-white/85 drop-shadow-sm">
                {description}
              </p>
            )}

            {/* Buttons Row (Prime Video style) */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              {/* Primary Watch / Play Button */}
              <Link
                href={`/watch/${id}`}
                className="h-12 rounded-lg bg-white/20 hover:bg-white/30 border border-white/20 px-5 text-white backdrop-blur-md shadow-xl flex items-center gap-3 transition-all group/btn cursor-pointer"
              >
                <Icon icon="ph:play-fill" className="size-5 text-white shrink-0 group-hover/btn:scale-110 transition-transform" />
                <div className="flex flex-col items-start text-left leading-tight">
                  <span className="text-lg font-bold text-white tracking-wide">Watch with Kast</span>
                </div>
              </Link>

              {/* My List circular button */}
              <MyListButton title={title} size="lg" />

              {/* Details circular button */}
              <Button
                asChild
                variant="outline"
                className="size-12 rounded-full border-white/25 bg-white/15 p-0 text-white hover:border-white/40 hover:bg-white/25 hover:text-white backdrop-blur-md cursor-pointer transition-transform active:scale-95"
              >
                <Link href={`/movie/${id}`} aria-label="Details">
                  <Icon icon="ph:info-bold" className="size-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom-right info: Terms apply & Age rating badge */}
      {/* <div className="absolute bottom-6 right-6 sm:bottom-8 sm:right-10 hidden sm:flex items-center gap-3 text-xs text-white/60 z-20 pointer-events-none select-none">
        <span>Terms apply</span>
        <span className="px-1.5 py-0.5 rounded bg-white/15 border border-white/20 text-white/90 font-bold text-[11px]">
          16+
        </span>
      </div> */}

      {/* Mute / Unmute toggle in top-right */}
      {videoPlaying && (
        <div className="absolute top-24 right-6 z-20 sm:right-10">
          <button
            type="button"
            onClick={toggleMute}
            className="flex items-center justify-center size-11 rounded-full bg-black/50 border border-white/20 text-white/70 hover:text-white hover:bg-black/70 backdrop-blur-md transition-all cursor-pointer hover:scale-105"
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            <Icon icon={isMuted ? 'ph:speaker-slash' : 'ph:speaker-high'} className="size-5" />
          </button>
        </div>
      )}
    </div>
  );
}

export default HeroCarousel;
